import React, { useEffect, useState } from 'react'
import Styles from './InviteAdminModal.module.css'
import styled from 'styled-components'
import Colors from 'Theme/Colors'
import { useTranslation } from 'react-i18next'
import IFButton from 'Components/IFButton/IFButton'
import {
  TextField,
  FormGroup,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material'
import AuthSelectors from 'Stores/Auth/Selectors'
import { connect } from 'react-redux'
import { IFSelect, IFText } from 'Components'
import RequestState from 'Enums/RequestState'
import PropTypes from 'prop-types'
import AdminSelectors from 'Stores/Admin/Selectors'
import RoleActions from 'Stores/Role/Actions'
import RoleSelectors from 'Stores/Role/Selectors'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const StyledTextField = styled(TextField)({
  '& label.Mui-focused': {
    color: Colors.text,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: Colors.grey,
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: Colors.primary,
      borderWidth: 1,
    },
    '&.Mui-focused fieldset': {
      borderColor: Colors.primary,
      borderWidth: 1,
    },
  },
  width: '17.5rem',
  '& .MuiInputBase-input': {
    height: '20px',
    padding: '10px',
  },
})
const StyledSelect = styled(Select)`
  && {
    font-family: 'ProximaNova';
    font-size: 14px;
    color: ${Colors.text};
    height: 42px;
    width: 17.5rem;
  }
`
const StyledFormControl = styled(FormControl)({
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: Colors.primary,
    },
    '& fieldset': {
      borderColor: Colors.grey,
      borderWidth: 1,
    },
    '&.Mui-focused fieldset': {
      borderColor: Colors.primary,
    },
  },
  width: '100%',
})
const InviteAdminModal = ({
  loggedInAdmin,
  onSubmitForm,
  inviteAdminRequestState,
  fetchAllRoles,
  allRoles,
  fetchAllRolesRequestState,
  allRolesPaginationOffset,
  clearRoles,
}) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [selectedCountries, setSelectedCountries] = useState([])
  const [selectedRole, setSelectedRole] = useState('')
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [emailErrorText, setEmailErrorText] = useState('')
  const [countryError, setCountryError] = useState(false)
  const [countryErrorText, setCountryErrorText] = useState('')
  const [roleError, setRoleError] = useState(false)
  const [roleErrorText, setRoleErrorText] = useState('')
  const [isCountryTouched, setIsCountryTouched] = useState(false)

  const handleEmailChange = (event) => {
    const value = event.target.value
    setEmail(value)

    if (!value) {
      setEmailError(true)
      setEmailErrorText(t('AdminPage.FieldRequired'))
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      setEmailError(true)
      setEmailErrorText(t('AdminPage.InvalidEmail'))
    } else {
      setEmailError(false)
      setEmailErrorText('')
    }
  }

  const handleCountryChange = (event) => {
    setIsCountryTouched(true)
    if (loggedInAdmin.registeredCountries.length > 1) {
      setSelectedCountries(event.target.value)
      setCountryError(false)
      setCountryErrorText('')
      setSelectedRole('')
    }
  }

  const handleCountryBlur = () => {
    setIsCountryTouched(true)
    if (selectedCountries.length === 0) {
      setCountryError(true)
      setCountryErrorText(t('AdminPage.FieldRequired'))
    }
  }

  const handleSubmit = () => {
    if (!email) {
      setEmailError(true)
      setEmailErrorText(t('AdminPage.FieldRequired'))
      return
    }
    if (emailError) return
    if (
      selectedCountries.length === 0 &&
      loggedInAdmin.registeredCountries.length > 1
    ) {
      setCountryError(true)
      setCountryErrorText(t('AdminPage.FieldRequired'))
      return
    }
    if (!selectedRole) {
      setRoleError(true)
      setRoleErrorText(t('AdminPage.FieldRequired'))
      return
    }

    onSubmitForm({
      email,
      countries: selectedCountries,
      role: selectedRoleId,
    })
  }

  const isButtonDisabled =
    !email ||
    emailError ||
    (selectedCountries.length === 0 &&
      loggedInAdmin.registeredCountries.length > 1) ||
    !selectedRole

  const loadMoreData = () => {
    if (
      fetchAllRolesRequestState === RequestState.LOADING ||
      allRolesPaginationOffset === null
    )
      return
    fetchAllRoles(allRolesPaginationOffset, false)
  }

  useEffect(() => {
    if (
      isCountryTouched &&
      loggedInAdmin.registeredCountries.length > 1 &&
      selectedCountries.length === 0
    ) {
      setSelectedRole('')
      setRoleError(true)
      setRoleErrorText(t('AdminPage.InviteAdminCountryRequired'))
    } else {
      setRoleError(true)
      setRoleErrorText('')
    }
  }, [selectedCountries, loggedInAdmin.registeredCountries.length, t])

  return (
    <div className={Styles.InviteAdminContainer}>
      <div>
        <FormGroup className={Styles.InputWrappingDiv}>
          <IFText>{t('AdminPage.InviteAdminEmail')}</IFText>
          <StyledTextField
            value={email}
            onChange={handleEmailChange}
            error={emailError}
          />
        </FormGroup>
        <div className={Styles.ErrorText} style={{ color: Colors.red }}>
          {emailError && emailErrorText}
        </div>
      </div>

      {loggedInAdmin.registeredCountries.length > 1 && (
        <FormGroup className={Styles.InputWrappingDivCountry}>
          <StyledFormControl>
            <IFText>{t('AdminPage.InviteAdminCountry')}</IFText>
            <StyledSelect
              multiple
              value={selectedCountries}
              onChange={handleCountryChange}
              onBlur={handleCountryBlur}
              renderValue={(selected) =>
                selected
                  .map(
                    (countryId) =>
                      loggedInAdmin.registeredCountries.find(
                        (country) => country.id === countryId,
                      )?.name,
                  )
                  .join(', ')
              }
              IconComponent={ExpandMoreIcon}
              sx={{
                '.MuiSelect-icon': {
                  marginRight: '6px',
                  fontSize: '20px',
                  fontWeight: 'thin',
                },
              }}
            >
              {loggedInAdmin.registeredCountries.map((country) => (
                <MenuItem key={country.id} value={country.id}>
                  {country.name}
                </MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>
          {countryError && (
            <div className={Styles.ErrorText} style={{ color: Colors.red }}>
              {countryErrorText}
            </div>
          )}
        </FormGroup>
      )}

      <StyledFormControl>
        <IFText>{t('AdminPage.InviteAdminRole')}</IFText>

        <IFSelect
          key={selectedCountries.join(',')}
          paginationOffset={allRolesPaginationOffset}
          size={'lg'}
          data={(loggedInAdmin.registeredCountries.length > 1
            ? allRoles.filter((role) =>
                selectedCountries.includes(role.country),
              )
            : allRoles
          ).map((role) => ({
            label: role.name,
            value: role.name,
          }))}
          value={selectedRole}
          onChange={(selectedValue) => {
            const selectedRoleObject = allRoles.find(
              (role) => role.name === selectedValue,
            )
            setSelectedRole(selectedValue)
            setSelectedRoleId(selectedRoleObject?.id)
            setRoleError(false)
            setRoleErrorText('')
          }}
          onSearch={(query) => {
            fetchAllRoles(0, false, query)
          }}
          loadMore={() => {
            loadMoreData()
          }}
          loading={fetchAllRolesRequestState === RequestState.LOADING}
          disabled={
            loggedInAdmin.registeredCountries.length > 1 &&
            selectedCountries.length === 0
          }
          style={{
            width: '100%',
          }}
          handleMenuClose={() => {
            clearRoles()
          }}
          setError={(errorMessage) => {
            if (selectedCountries.length > 0) {
              setRoleErrorText(errorMessage)
              setRoleError(!!errorMessage)
            }
          }}
        />
        {roleError && (
          <div className={Styles.ErrorText} style={{ color: Colors.red }}>
            {roleErrorText}
          </div>
        )}
      </StyledFormControl>

      <IFButton
        size="sm"
        color={Colors.primary}
        isFill={true}
        text={t('AdminPage.InviteAdminButton')}
        className={Styles.SaveButton}
        onClick={handleSubmit}
        isDead={isButtonDisabled}
        isLoading={inviteAdminRequestState === RequestState.LOADING}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  loggedInAdmin: AuthSelectors.getUser(state),
  inviteAdminRequestState: AdminSelectors.getInviteAdminRequestState(state),
  allRoles: RoleSelectors.getAllRoles(state),
  fetchAllRolesRequestState: RoleSelectors.getFetchAllRolesRequestState(state),
  allRolesPaginationOffset: RoleSelectors.getAllRolesPaginationOffset(state),
})

function mapDispatchToProps(dispatch) {
  return {
    fetchAllRoles: (offset, isFiltering, roleName) =>
      dispatch(RoleActions.fetchAllRoles(offset, isFiltering, roleName)),
    clearRoles: () => dispatch(RoleActions.clearAllRoles()),
  }
}
InviteAdminModal.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  loggedInAdmin: PropTypes.object,
  inviteAdminRequestState: PropTypes.number,
  fetchAllRoles: PropTypes.func,
  allRoles: PropTypes.array,
  fetchAllRolesRequestState: PropTypes.number,
  allRolesPaginationOffset: PropTypes.number,
}
export default connect(mapStateToProps, mapDispatchToProps)(InviteAdminModal)
